import React, { useState } from 'react';
import { Modal, Box } from '@mui/material';
import useLocationsIntegrationsHoursStore from '../stores/locationsIntegrationsHoursStore';
import useCreateCompanyStore from '../stores/createCompanyStore';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { API_load_sandbox_data } from '../constants';
import { getAuthToken } from '../utilities/requests';

// Page 1: Welcome and Credentials
const WelcomePage = ({ userEmail, onGetStarted }) => (
  <>
    {/* Header */}
    <div className="px-8 pt-8 pb-6">
      <div className="flex flex-col items-center text-center space-y-4">
        <div className="w-16 h-16 bg-violet-700 rounded-full flex items-center justify-center">
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="text-2xl font-light text-gray-900">
          Welcome to Rightwork
        </h2>
        <p className="text-gray-600 text-base font-light">
          Your company has been successfully created
        </p>
      </div>
    </div>

    {/* Content */}
    <div className="px-8 pb-8 space-y-6">
      <div className="bg-gray-50 rounded-2xl p-6">
        <h4 className="text-gray-900 font-medium mb-4 text-center">Account Access</h4>
        <div className="space-y-4">
          <div className="space-y-2">
            <span className="text-gray-900 font-medium text-sm">Email</span>
            <div className="bg-white rounded-xl px-4 py-3 border border-gray-200">
              <span className="text-gray-900 font-mono text-sm">
                {userEmail}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-amber-50 rounded-2xl p-6">
        <div className="flex items-start space-x-3">
          <div className="w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="text-sm text-gray-700">
            <p className="font-medium mb-1">Keep your account secure</p>
            <p className="text-gray-600">Use "Forgot Password" on the login screen if you need to reset access</p>
          </div>
        </div>
      </div>

      {/* Action Button */}
      <div className="pt-2">
        <button
          onClick={onGetStarted}
          className="w-full px-4 py-3 bg-violet-700 hover:bg-violet-800 text-white rounded-xl font-medium text-base transition-all shadow-lg hover:shadow-xl"
        >
          Get Started
        </button>
      </div>
    </div>
  </>
);

// Page 2: POS Integration Choice
const POSIntegrationPage = ({ selectedOption, onOptionSelect, onGo, isLoading }) => (
  <>
    {/* Header */}
    <div className="px-8 pt-8 pb-6">
      <div className="flex flex-col items-center text-center space-y-4">
        <div className="w-16 h-16 bg-violet-700 rounded-full flex items-center justify-center">
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        </div>
        <h2 className="text-2xl font-light text-gray-900">
          Connect To Data
        </h2>
        <p className="text-gray-600 text-base font-light">
          Sync real data or explore with sample data first
        </p>
      </div>
    </div>

    {/* Content */}
    <div className="px-8 pb-8 space-y-6">
      <div className="space-y-4">
        <div
          className={`bg-white rounded-2xl p-6 border-2 transition-all cursor-pointer ${
            selectedOption === 'connect'
              ? 'border-violet-500 bg-violet-50'
              : 'border-gray-200 hover:border-gray-300'
          }`}
          onClick={() => onOptionSelect('connect')}
        >
          <div className="flex items-start space-x-4">
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${
              selectedOption === 'connect' ? 'bg-violet-200' : 'bg-gray-100'
            }`}>
              <svg className={`w-6 h-6 ${selectedOption === 'connect' ? "text-violet-700" : "text-gray-600"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div className="flex-1">
              <div className="flex items-center space-x-2">
                {/* <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                  selectedOption === 'connect'
                    ? 'border-violet-500 bg-violet-500'
                    : 'border-gray-300'
                }`}>
                  {selectedOption === 'connect' && (
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  )}
                </div> */}
                <h4 className="text-gray-900 font-medium">Connect Your POS</h4>
              </div>
              <p className="text-gray-600 text-sm mt-2">
                Set up real-time data sync with your existing POS system
              </p>
            </div>
          </div>
        </div>

        <div
          className={`bg-white rounded-2xl p-6 border-2 transition-all cursor-pointer ${
            selectedOption === 'sample'
              ? 'border-violet-500 bg-violet-50'
              : 'border-gray-200 hover:border-gray-300'
          }`}
          onClick={() => onOptionSelect('sample')}
        >
          <div className="flex items-start space-x-4">
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${
              selectedOption === 'sample' ? 'bg-violet-200' : 'bg-gray-100'
            }`}>
              <svg className={`w-6 h-6 ${selectedOption === 'sample' ? "text-violet-700" : "text-gray-600"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <div className="flex-1">
              <div className="flex items-center space-x-2">
                {/* <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                  selectedOption === 'sample'
                    ? 'border-violet-500 bg-violet-500'
                    : 'border-gray-300'
                }`}>
                  {selectedOption === 'sample' && (
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  )}
                </div> */}
                <h4 className="text-gray-900 font-medium">Explore with Sample Data</h4>
              </div>
              <p className="text-gray-600 text-sm mt-2">
                Test all features with demo data and connect your POS later
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Action Button */}
      <div className="pt-2">
        <button
          onClick={onGo}
          disabled={!selectedOption || isLoading}
          className={`w-full px-4 py-3 rounded-xl font-medium text-base transition-all flex items-center justify-center ${
            selectedOption && !isLoading
              ? 'bg-violet-700 hover:bg-violet-800 text-white shadow-lg hover:shadow-xl'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          {isLoading ? (
            <>
              <svg className="w-5 h-5 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Loading Sample Data...
            </>
          ) : (
            'Go'
          )}
        </button>
      </div>
    </div>
  </>
);

// Main Modal Component
const WelcomeModal = ({ open, onClose, userEmail, setCurrentTab }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOption, setSelectedOption] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { setDisplay } = useLocationsIntegrationsHoursStore();
  const { resetForm } = useCreateCompanyStore();

  const handleClose = () => {
    setCurrentPage(1); // Reset to first page
    setSelectedOption(null); // Reset selection
    setIsLoading(false); // Reset loading state
    onClose();
    resetForm(); // Reset the create company form
  };

  const handleGetStarted = () => {
    setCurrentPage(2);
  };

  const handleOptionSelect = (option) => {
    setSelectedOption(option);
  };

  const handleGo = async () => {
    if (selectedOption === 'connect') {
      // Navigate to POS integration settings and set display to Integrations tab
      setCurrentTab("locationsIntegrationsHoursSettings");
      setDisplay("Integrations");
      handleClose();
    } else if (selectedOption === 'sample') {
      // Load sandbox data from Test company
      setIsLoading(true);
      try {
        const token = getAuthToken();
        await axios.post(
          API_load_sandbox_data,
          {},
          {
            headers: {
              'Authorization': `Token ${token}`,
              'Content-Type': 'application/json',
            }
          }
        );
        // Success - close modal and let user explore
        handleClose();
        // Optionally show a success message or redirect
        window.location.reload(); // Reload to show the new data
      } catch (error) {
        console.error('Error loading sandbox data:', error);
        alert('Failed to load sandbox data. Please try again.');
        setIsLoading(false);
      }
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="welcome-modal"
      aria-describedby="welcome-modal-description"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '100%',
          maxWidth: 500, // max-w-md equivalent
          bgcolor: 'background.paper',
          borderRadius: 6, // rounded-3xl equivalent
          boxShadow: 24,
          outline: 'none',
          mx: 2, // margin on mobile
        }}
      >
        {/* Close Button */}
        <div className="relative">
          <button
            className="absolute right-4 top-4 w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors z-10"
            onClick={handleClose}
          >
            <CloseIcon className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        {/* Modal Content */}
        {currentPage === 1 ? (
          <WelcomePage
            userEmail={userEmail}
            onGetStarted={handleGetStarted}
          />
        ) : (
          <POSIntegrationPage
            selectedOption={selectedOption}
            onOptionSelect={handleOptionSelect}
            onGo={handleGo}
            isLoading={isLoading}
          />
        )}
      </Box>
    </Modal>
  );
};

export default WelcomeModal;
